import { dealFilterDesc, isLogicalFilter, Show, useDataGrid, useInvalidate, useToggle, useUpdate } from 'admin';
import type { Load } from 'api';
import type { Dayjs } from 'date';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, DataGrid, Drawer, Page, Section } from 'ui';

import { DealDetails } from '~/deal';
import { createFromFilter, createToFilter, initialFromFilter, TimeFilter } from '~/time-filter';

import { LoadDetails } from '../../components/LoadDetails/LoadDetails';
import { useSearchedLoad } from '../../hooks/useSearchedLoad/useSearchedLoad';
import { columns } from './columns';
import { TIME_FILTER_FIELD_NAME } from './constants';

export const LoadsList = () => {
  const { mutate } = useUpdate<Load>();
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const { data: searchedLoad } = useSearchedLoad();

  const { open, isOpen, close } = useToggle();

  const [selectedLoad, setSelectedLoad] = useState<Load | null>(null);

  const { dataGridProps, filters, setFilters } = useDataGrid({
    filters: {
      initial: [initialFromFilter(TIME_FILTER_FIELD_NAME)],
      mode: 'server',
    },
    resource: '/inspections/v1/loads/',
    sorters: {
      initial: [dealFilterDesc],
      mode: 'server',
    },
    syncWithLocation: false,
  });

  useEffect(() => {
    if (searchedLoad) {
      setSelectedLoad(searchedLoad.data);
      open();
    }
  }, [open, searchedLoad]);

  const nonTimeFilters = useMemo(
    () =>
      filters.filter((filter) => {
        if (!isLogicalFilter(filter)) {
          return true;
        }
        return filter.field !== TIME_FILTER_FIELD_NAME;
      }),
    [filters]
  );

  const handleTimeFilterChange = useCallback(
    ({ from, to }: { from: Dayjs; to: Dayjs }) => {
      setFilters([
        ...nonTimeFilters,
        createFromFilter(from, TIME_FILTER_FIELD_NAME),
        createToFilter(to, TIME_FILTER_FIELD_NAME),
      ]);
    },
    [nonTimeFilters, setFilters]
  );

  const markInspectionNotNeeded = (loadId: number) => {
    mutate(
      {
        dataProviderName: 'inspections',
        id: `${loadId}/mark_inspection_not_needed`,
        resource: `/v1/loads`,
        successNotification: {
          description: 'Success',
          message: 'Load marked as not needing inspection',
          type: 'success',
        },
        values: {},
      },
      {
        onSuccess: (data) => {
          setSelectedLoad(data.data);
          invalidate({
            invalidates: ['list'],
            resource: '/inspections/v1/loads',
          });
        },
      }
    );
  };

  return (
    <Page>
      <Drawer open={isOpen} anchor="right">
        <Show
          title={`Load ${selectedLoad?.name}`}
          headerButtons={
            <Button
              size="small"
              onClick={() => {
                navigate(`/ground-control/inspections/create?dealId=${selectedLoad?.deal_id}`);
              }}
            >
              Create Inspection
            </Button>
          }
          onClose={close}
        >
          <Box mb={2}>
            <LoadDetails load={selectedLoad} onNotNeededClick={markInspectionNotNeeded} />
          </Box>
          {selectedLoad ? <DealDetails dealId={selectedLoad.deal_id} /> : null}
        </Show>
      </Drawer>
      <Section title="Loads">
        <TimeFilter onDateChange={handleTimeFilterChange} />
        <DataGrid<Load>
          {...dataGridProps}
          showColumnVerticalBorder
          showCellVerticalBorder
          autoHeight
          filterDebounceMs={500}
          density="standard"
          sx={{ overflowX: 'scroll' }}
          columns={columns}
          onRowClick={(params) => {
            setSelectedLoad(params.row);
            open();
          }}
        />
      </Section>
    </Page>
  );
};
